import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, Trash2, IndianRupee } from "lucide-react";
import { toast } from "sonner";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

export default function DeleteCustomersDialog({ customers, open, onOpenChange, onDeleted }) {
  const qc = useQueryClient();
  const [deleting, setDeleting] = useState(false);
  const ids = customers.map((c) => c.id);
  const single = customers.length === 1 ? customers[0] : null;

  const { data: impact, isLoading } = useQuery({
    queryKey: ["delete-impact", ids],
    queryFn: async () => (await api.post("/customers/delete-impact", { ids })).data,
    enabled: open && ids.length > 0,
    gcTime: 0,
  });

  const confirm = async () => {
    setDeleting(true);
    try {
      const r = await api.post("/customers/bulk-delete", { ids });
      toast.success(r.data.deleted === 1 ? "Customer deleted" : `${r.data.deleted} customers deleted`);
      ["customers", "dashboard", "reports", "goals", "followups"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      onDeleted?.(r.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!deleting) onOpenChange(o); }}>
      <DialogContent className="sm:max-w-md" data-testid="delete-customer-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-red-700 flex items-center gap-2">
            <Trash2 className="h-5 w-5" /> {single ? "Delete customer" : `Delete ${customers.length} customers`}
          </DialogTitle>
          <DialogDescription>
            {single ? (
              <>This permanently deletes <span className="font-semibold text-stone-700">{single.name}</span> ({single.mobile}) and</>
            ) : (
              <>This permanently deletes the <span className="font-semibold text-stone-700">{customers.length} selected customers</span> and</>
            )}{" "}
            all their messages, responses, notes, follow-ups and history. This cannot be undone. To keep the record and just stop follow-ups, use <span className="font-medium">Archive</span> instead.
          </DialogDescription>
        </DialogHeader>

        {!single && <NameList customers={customers} />}
        {isLoading ? (
          <div className="flex justify-center py-2"><Loader2 className="h-4 w-4 animate-spin text-stone-400" /></div>
        ) : impact?.orders > 0 && <RevenueWarning impact={impact} />}
        {impact?.not_allowed > 0 && (
          <p className="text-xs text-stone-500" data-testid="delete-not-allowed-note">
            {impact.not_allowed} selected customer(s) are not assigned to you and will be skipped.
          </p>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={deleting} data-testid="delete-customer-cancel">Cancel</Button>
          <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={confirm} disabled={deleting || isLoading} data-testid="delete-customer-confirm">
            {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Trash2 className="mr-1.5 h-4 w-4" /> Delete permanently</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NameList({ customers }) {
  const shown = customers.slice(0, 6);
  return (
    <div className="rounded-lg border border-stone-200 bg-stone-50 px-3 py-2 text-xs text-stone-600" data-testid="delete-name-list">
      {shown.map((c) => c.name).join(", ")}
      {customers.length > shown.length && <span className="text-stone-400"> and {customers.length - shown.length} more</span>}
    </div>
  );
}

function RevenueWarning({ impact }) {
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 p-3" data-testid="delete-revenue-warning">
      <div className="flex items-center gap-1.5 text-sm font-semibold text-red-700">
        <IndianRupee className="h-4 w-4" /> {inr(impact.revenue)} in confirmed orders will be removed
      </div>
      <p className="mt-1 text-xs text-red-700/90">
        {impact.with_orders.length} customer(s) have {impact.orders} confirmed order(s). Deleting removes this revenue from the dashboard, weekly goals, leaderboard and reports.
      </p>
      <ul className="mt-1.5 space-y-0.5 text-xs text-red-800">
        {impact.with_orders.slice(0, 5).map((p) => (
          <li key={p.id} className="flex justify-between gap-2"><span className="truncate">{p.name}</span><span className="font-semibold">{inr(p.revenue)}</span></li>
        ))}
      </ul>
    </div>
  );
}
